import {parseRef, WeaveObjectRef} from '@wandb/weave/react';
import React, {useContext, useMemo} from 'react';

import {useWFHooks} from '../../wfReactInterface/context';
import {CallSchema} from '../../wfReactInterface/wfDataModelHooksInterface';

const useCallRefs = (call: CallSchema) => {
  return useMemo(() => {
    const evaluationRef = call.rawSpan.inputs?.self || null;
    const modelRef = call.rawSpan.inputs?.model || null;
    const callOutput = call.rawSpan.output || {};
    return {evaluationRef, modelRef, callOutput};
  }, [call.rawSpan.inputs, call.rawSpan.output]);
};

const useEvaluationData = (evaluationRef: string | null) => {
  const {useRefsData} = useWFHooks();

  const evaluationData = useRefsData({
    refUris: evaluationRef ? [evaluationRef] : [],
  });

  const datasetRef = useMemo(() => {
    if (
      !evaluationData.loading &&
      evaluationData.result &&
      evaluationData.result.length > 0
    ) {
      return evaluationData.result[0]?.dataset || null;
    }
    return null;
  }, [evaluationData.loading, evaluationData.result]);

  const evaluationObjectData = useMemo(() => {
    if (
      !evaluationData.loading &&
      evaluationData.result &&
      evaluationData.result.length > 0
    ) {
      return evaluationData.result[0] || null;
    }
    return null;
  }, [evaluationData.loading, evaluationData.result]);

  return {datasetRef, evaluationObjectData};
};

const useDatasetParsedRef = (datasetRef: string | null) => {
  return useMemo(() => {
    if (!datasetRef) {
      return null;
    }
    return parseRef(datasetRef) as WeaveObjectRef;
  }, [datasetRef]);
};

const useEvaluationDefinition = (call: CallSchema) => {
  const {evaluationRef, modelRef, callOutput} = useCallRefs(call);
  const {datasetRef, evaluationObjectData} = useEvaluationData(evaluationRef);
  const datasetParsedRef = useDatasetParsedRef(datasetRef);

  return {
    evaluationRef,
    modelRef,
    datasetRef,
    callOutput,
    datasetParsedRef,
    evaluationObjectData,
  };
};

type EvalTabData = {
  call: CallSchema;
  evaluationDefinition: ReturnType<typeof useEvaluationDefinition>;
};

const EvalTabDataContext = React.createContext<EvalTabData | null>(null);

export const EvalTabDataProvider: React.FC<{
  call: CallSchema;
  children: React.ReactNode;
}> = ({call, children}) => {
  const evaluationDefinition = useEvaluationDefinition(call);

  const value = useMemo(
    () => ({
      call,
      evaluationDefinition,
    }),
    [call, evaluationDefinition]
  );

  return (
    <EvalTabDataContext.Provider value={value}>
      {children}
    </EvalTabDataContext.Provider>
  );
};

export const useEvalTabData = () => {
  const ctx = useContext(EvalTabDataContext);
  if (ctx == null) {
    throw new Error('useEvalTabData must be used within EvalTabDataProvider');
  }
  return ctx;
};
